import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import logo from "../../assets/logo.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Programs" },
  { to: "/placements", label: "Placements" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/admissions", label: "Admissions" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_8px_32px_rgba(10,30,77,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div className="bg-white rounded-md p-1.5 shadow-sm ring-1 ring-gold/30">
            <img src={logo} alt="SVS College of Nursing — Care, Compassion, Commitment" className="h-14 sm:h-16 w-auto object-contain" />
          </div>
          <div className="leading-tight">
            <div className={`font-serif text-lg sm:text-xl lg:text-[22px] font-bold uppercase tracking-tight whitespace-nowrap transition-colors duration-300 ${scrolled ? "text-navy" : "text-sand"}`}>
              SVS College of Nursing
            </div>
            <div className="text-[11px] tracking-[0.28em] text-gold uppercase">Bokaro, Jharkhand</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
                  isActive ? "text-gold" : scrolled ? "text-navy" : "text-sand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admissions"
            data-testid="header-apply-btn"
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full text-sm font-semibold px-5 py-2.5 transition-[transform,background-color,color] duration-300 hover:scale-105 ${
              scrolled ? "bg-navy text-sand hover:bg-navy-light" : "bg-gold text-navy-dark"
            }`}
          >
            Apply Now <ArrowUpRight size={16} className={scrolled ? "text-gold" : "text-navy-dark"} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors duration-300 ${scrolled ? "text-navy" : "text-sand"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-border"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="text-navy font-medium text-base"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/admissions" className="mt-2 rounded-full bg-navy text-sand text-center font-semibold px-5 py-3">
                Apply Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
